import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Bot, LineChart, Shield, Wallet, ArrowRight, ChevronLeft, ChevronRight, TrendingUp, Zap, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

const FALLBACK_TICKER = [
  { symbol: "BTC", name: "Bitcoin", price: 67240.1, change: 1.8, image: "" },
  { symbol: "ETH", name: "Ethereum", price: 3512.44, change: 2.3, image: "" },
  { symbol: "BNB", name: "BNB", price: 586.2, change: -0.6, image: "" },
  { symbol: "SOL", name: "Solana", price: 165.77, change: 4.1, image: "" },
  { symbol: "XRP", name: "XRP", price: 0.6231, change: -1.2, image: "" },
  { symbol: "ADA", name: "Cardano", price: 0.4488, change: -0.9, image: "" },
  { symbol: "DOGE", name: "Dogecoin", price: 0.1642, change: 3.7, image: "" },
  { symbol: "AVAX", name: "Avalanche", price: 36.92, change: 2.0, image: "" },
  { symbol: "LTC", name: "Litecoin", price: 84.15, change: 1.1, image: "" },
  { symbol: "TRX", name: "TRON", price: 0.1304, change: 0.4, image: "" },
];

const features = [
  { icon: LineChart, title: "Pro Trading Terminal", desc: "Candlestick, Heikin-Ashi, 13 indicators and drawing tools on a full-screen professional chart." },
  { icon: Bot, title: "AI Trading Bots", desc: "Activate licensed automated strategies and test them thoroughly in demo before risking real capital." },
  { icon: Wallet, title: "Demo & Live Wallets", desc: "Completely separated environments. Demo funds can never touch live money — practice with confidence." },
  { icon: Shield, title: "Bank-grade Security", desc: "2FA, session management and server-side validation on every trade, wallet and financial operation." },
];

function fmtPrice(p) {
  if (p >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (p >= 1) return p.toFixed(2);
  return p.toFixed(4);
}

export default function Landing() {
  const { user } = useAuth();
  const { general, slides: apiSlides } = useSettings();
  const authed = user && typeof user === "object";
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [ticker, setTicker] = useState(FALLBACK_TICKER);
  const tickerLoaded = useRef(false);

  const fallbackSlides = [
    { tag: "AI TRADING · DEMO & LIVE", title: "Trade Smarter. Explore the Future of AI Trading.", sub: "A professional trading environment with Demo and Live trading, powerful analytics and automated trading technology.", img: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", b1: { label: "OPEN TRADING TERMINAL", to: authed ? "/terminal" : "/register" }, b2: { label: "VIEW DASHBOARD", to: authed ? "/dashboard" : "/login" } },
    { tag: "AUTOMATED STRATEGIES", title: "Automate Your Trading With AI-Powered Bots", sub: "Test trading strategies in Demo Mode, monitor bot performance and activate licensed bots when you're ready.", img: "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", b1: { label: "EXPLORE TRADING BOTS", to: authed ? "/bots" : "/register" }, b2: { label: "VIEW DASHBOARD", to: authed ? "/dashboard" : "/login" } },
    { tag: "PRACTICE FIRST", title: "Practice in Demo. Trade Live When You're Ready.", sub: "Explore the markets and evaluate automated strategies using virtual funds before moving to live trading.", img: "https://images.unsplash.com/photo-1651341050677-24dba59ce0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", b1: { label: "START DEMO", to: authed ? "/dashboard" : "/register" }, b2: { label: "GO TO TERMINAL", to: authed ? "/terminal" : "/login" } },
  ];

  const slides = (apiSlides && apiSlides.length > 0)
    ? apiSlides.map((s) => ({ tag: s.subtitle, title: s.title, sub: s.description, img: s.image, b1: { label: s.b1_text, to: s.b1_url || "/register" }, b2: { label: s.b2_text, to: s.b2_url || "/login" } }))
    : fallbackSlides;

  useEffect(() => { setSlide(0); }, [apiSlides]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  useEffect(() => {
    if (tickerLoaded.current) return;
    tickerLoaded.current = true;
    const ctrl = new AbortController();
    fetch("https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=12&page=1&price_change_percentage=24h", { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (Array.isArray(data) && data.length) {
          setTicker(data.map((c) => ({ symbol: (c.symbol || "").toUpperCase(), name: c.name, price: c.current_price, change: c.price_change_percentage_24h ?? 0, image: c.image })));
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  const cur = slides[slide] || slides[0];
  const tickerLoop = [...ticker, ...ticker];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <PublicHeader />

      {/* HERO — dark */}
      <section className="relative overflow-hidden min-h-[600px] flex items-center bg-slate-900" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="hero-slider">
        {slides.map((sl, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
            <div className="absolute inset-0" style={{ backgroundImage: `url(${sl.img})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(15,23,42,0.92), rgba(15,23,42,0.70))" }} />
          </div>
        ))}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl" key={slide}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-mono text-blue-200 mb-6 animate-fade-up">
              <span className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot" /> {cur.tag}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight text-white animate-fade-up">{cur.title}</h1>
            <p className="text-lg text-slate-200 mt-6 max-w-xl leading-relaxed animate-fade-up">{cur.sub}</p>
            <div className="flex flex-wrap gap-3 mt-9 animate-fade-up">
              <Link to={cur.b1.to}><Button data-testid="hero-cta-primary" className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2 shadow-[0_4px_14px_0_rgba(59,130,246,0.5)]">{cur.b1.label} <ArrowRight className="h-4 w-4" /></Button></Link>
              <Link to={cur.b2.to}><Button data-testid="hero-cta-secondary" className="h-12 px-6 bg-white/10 hover:bg-white/20 text-white border border-white/20">{cur.b2.label}</Button></Link>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-12">
            <button onClick={() => setSlide((s) => (s - 1 + slides.length) % slides.length)} data-testid="hero-prev" className="h-9 w-9 rounded-full border border-white/20 bg-white/10 grid place-items-center text-white hover:bg-white/20 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setSlide(i)} data-testid={`hero-dot-${i}`} className={`h-2 rounded-full transition-all ${i === slide ? "w-8 bg-blue-500" : "w-2 bg-white/30 hover:bg-white/50"}`} />
              ))}
            </div>
            <button onClick={() => setSlide((s) => (s + 1) % slides.length)} data-testid="hero-next" className="h-9 w-9 rounded-full border border-white/20 bg-white/10 grid place-items-center text-white hover:bg-white/20 transition-colors"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </section>

      {/* CRYPTO TICKER — light, auto-scroll */}
      <div className="bg-white border-b border-slate-200 overflow-hidden marquee-paused" data-testid="crypto-ticker">
        <div className="flex w-max animate-marquee">
          {tickerLoop.map((t, i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-4 whitespace-nowrap border-r border-slate-100">
              {t.image ? (
                <img src={t.image} alt={t.symbol} className="h-7 w-7 rounded-full" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-blue-100 grid place-items-center text-blue-700 text-[10px] font-bold">{t.symbol.slice(0, 3)}</div>
              )}
              <div>
                <div className="text-xs font-semibold text-slate-900">{t.symbol}<span className="text-slate-400 font-normal">/USD</span></div>
                <div className="font-mono text-sm text-slate-700">${fmtPrice(t.price)}</div>
              </div>
              <span className={`font-mono text-xs font-semibold ${t.change >= 0 ? "text-emerald-600" : "text-red-500"}`}>{t.change >= 0 ? "▲" : "▼"} {Math.abs(t.change).toFixed(2)}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES — light */}
      <section className="bg-slate-50 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-blue-600 text-xs font-mono uppercase tracking-widest mb-3"><Zap className="h-4 w-4" /> Built for serious practice</div>
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 max-w-2xl">Everything a modern trader needs, minus the risk.</h2>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg">A complete, transparent environment to learn, practice and automate — before a single dollar is on the line.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {features.map((f) => (
              <div key={f.title} className="group rounded-2xl bg-white border border-slate-200 p-6 shadow-[0_1px_3px_rgba(15,23,42,0.05)] hover:shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)] hover:-translate-y-1 transition-all duration-300" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
                <div className="h-12 w-12 rounded-xl bg-blue-50 grid place-items-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-bold mt-5 text-slate-900">{f.title}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS — dark */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[["$10K", "Virtual funds"], ["13", "Pro indicators"], ["7+", "Live markets"], ["<1s", "Trade execution"]].map(([v, l]) => (
              <div key={l} className="text-center md:text-left">
                <div className="font-mono text-4xl font-bold text-white">{v}</div>
                <div className="text-slate-400 text-sm mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY / TRUST — light */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 text-blue-600 text-xs font-mono uppercase tracking-widest mb-3"><TrendingUp className="h-4 w-4" /> Why traders choose us</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Transparency and education, first.</h2>
            <div className="mt-8 space-y-4">
              {["Strict Demo / Live separation — practice money never touches real funds", "Transparent, clearly-labelled simulated results in demo mode", "Real market-style movement so your practice actually transfers", "Server-side validation on every single financial operation"].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  <p className="text-slate-700">{t}</p>
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-block mt-8"><Button variant="outline" data-testid="learn-more-btn" className="border-slate-300 text-slate-800 hover:bg-slate-50 gap-2">Learn more about us <ArrowRight className="h-4 w-4" /></Button></Link>
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
            <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Trading analytics" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CTA — brand */}
      <section className="bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight text-white">Ready to open your demo account?</h2>
          <p className="text-blue-100 mt-3 max-w-lg mx-auto text-lg">Instant access. No email verification wall. Practice with real market-style movement today.</p>
          <Link to="/register" className="inline-block mt-8">
            <Button data-testid="cta-register-btn" className="h-12 px-8 bg-white text-blue-700 hover:bg-blue-50 font-semibold gap-2">Create free account <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
